import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

import { ConfigService } from '../../core/services/config.service';
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private config = inject(ConfigService);

  private readonly authService = inject(AuthService);
  private apiUrl = environment;
  private readonly CREATE_CUSTOMER_URL = 'https://api.withmono.com/v2/customers';
  private readonly INITIATE_PAYMENT = 'https://api.withmono.com/v2/payments/initiate';
  private readonly CANCEL_MANDATE = 'https://api.withmono.com/v3/payments/mandates';
  private readonly PAYSTACK_URLS: string[] = [];

  private get publicPatterns(): RegExp[] {
    const apiBaseUrl = this.config.get('COLLECT_API_BASE_URL');
    const omniBaseUrl = this.config.get('OMNI_API_BASE_URL');
    
    return [
      new RegExp(`^${apiBaseUrl}${this.apiUrl.JOB_ROLE}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.LICENSE}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.STATES}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.LGA}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.NUMBER_OF_CUSTOMERS}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.PAYMENT_COLLECTION_METHOD}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.PAYMENT_COLLECTION_METHOD_OPTIONS}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.EMAIL_TOKEN}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.VERIFY_EMAIL_TOKEN}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.SERVICES}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.LOGIN}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.FORGOT_PASSWORD}`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.RESET_PASSWORD}`),
      
      new RegExp(`^${apiBaseUrl}/users/registration/complete`),
      
      new RegExp(`^${apiBaseUrl}/customer/update`),
      new RegExp(`^${apiBaseUrl}/organisations/customer/update`),
      new RegExp(`^${apiBaseUrl}/tokens/monoToken`),
      // ARCHIVED Paystack DD (kept for archived flow compatibility)
      new RegExp(`^${apiBaseUrl}/tokens/paystack-dd/initialize`),
      new RegExp(`^${apiBaseUrl}/v2/mandate/customer/.+/paystack-direct-debit`),
      new RegExp(`^${apiBaseUrl}/dashboard/paystack/mandate/reuse`),
      new RegExp(`^${apiBaseUrl}/loans/updateMonoMandate`),
      new RegExp(`^${apiBaseUrl}/loans/public/[0-9a-fA-F-]{36}$`),
      new RegExp(`^${apiBaseUrl}/loans/public/mandate-reuse/`),
      new RegExp(`^${apiBaseUrl}/mono/`),
      new RegExp(`^${apiBaseUrl}${this.apiUrl.ORG_DETAILS}`),
      new RegExp(`^${apiBaseUrl}/organisations/setting`),

      new RegExp(`^${omniBaseUrl}/v1/proof-of-payments/customer`),
      new RegExp(`^${omniBaseUrl}/v1/loans/limited`),

      new RegExp(`^${this.CREATE_CUSTOMER_URL}`),
      new RegExp(`^${this.INITIATE_PAYMENT}`),
      new RegExp(`^${this.CANCEL_MANDATE}`),
      new RegExp(`^${omniBaseUrl}${this.apiUrl.DETAILS}`),
    ];
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();

    if (
      this.isPaystackRequest(req) ||
      this.isRefreshTokenRequest(req) ||
      this.isPublicRoute(req.url) ||
      req.url.includes('/assets/config.json')
    ) {
      return next.handle(req);
    }

    if (token && !this.authService.isTokenExpired(30)) {
      return next.handle(this.addToken(req, token));
    }

    return from(this.authService.handleTokenRefresh()).pipe(
      switchMap((newToken) => next.handle(this.addToken(req, newToken))),
      catchError((err) => {
        // Don't bounce public pages (e.g. loan tokenisation) to login on auth failure.
        if (!this.authService.isExcludedFromSessionTimeout()) {
          this.authService.logout();
        } else {
          this.authService.clearSessionTimeout();
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('currentUser');
        }
        return throwError(() => err);
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private isPaystackRequest(req: HttpRequest<any>): boolean {
    return this.PAYSTACK_URLS.some((url) => req.url.startsWith(url));
  }

  private isRefreshTokenRequest(req: HttpRequest<any>): boolean {
    return req.url.includes(this.apiUrl.REFRESH_TOKEN);
  }

  private isPublicRoute(url: string): boolean {
    return this.publicPatterns.some((pattern) => pattern.test(url));
  }
}
